import { ExternalLinkIcon } from '@components/ExternalLinkIcon/ExternalLinkIcon';
import { GradientOverlay } from '@components/GradientOverlay/GradientOverlay';
import { AudioSelectedIcon } from '@components/Icons/AudioSelectedIcon';
import { SimilarSearchIcon } from '@components/Icons/SimilarSearchIcon';
import { SelectableCard } from '@components/SelectableCard/SelectableCard';
import { SelectionCheckbox } from '@components/SelectionCheckbox/SelectionCheckbox';
import { SoundToggle } from '@components/SoundToggle/SoundToggle';
import { TopOverlay } from '@components/TopOverlay/TopOverlay';
import { PlaceholderGradient } from '@components/util/PlaceholderGradient';
import { VideoPreview } from '@components/VideoPreview/VideoPreview';
import { isCompactCell } from '@components/VirtualMasonry/masonryConstants';
import { VirtualMasonryCellProps } from '@components/VirtualMasonry/VirtualMasonry';
import { useMediaFilePreview } from '@lib/hooks/preview/preview';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { getThumbnailFromVideo } from '@lib/image/fromVideo';
import { HLS_PLAYLIST_PATH } from '@lib/network/preview';
import { askAgentAboutMoments } from '@lib/starzero-agents';
import { generateIdFromString } from '@lib/utils/colorUtils';
import { isAudioMedia } from '@lib/utils/fileExtensions';
import { secondsToHMS } from '@lib/utils/time';
import { VERBS, verbMenuIcon } from '@lib/verbs';
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import { SearchResultItem, SegmentType } from '@stores/Library/LibrarySearch';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useMomentMergeStore } from '@stores/MomentMerge/MomentMergeStore';
import { encodeMomentToString } from '@stores/MomentSelection/MomentSelection';
import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import { useMomentModal } from '../../../../context/MomentModalContext';
import { useCenteredCard } from '../../CenteredCardContext';
import css from './Moment.module.scss';

export type MomentProps = {
  result: SearchResultItem;
  media: Media;
} & VirtualMasonryCellProps;

export const Moment = observer(function Moment({
  result,
  media,
  rowIndex,
  columnIndex,
  width,
}: MomentProps) {
  const { startTime, endTime } = result;
  const isMobile = useIsMobile();
  const libraryManager = useLibraryManager();
  const uiPreferences = useUIPreferences();
  const momentSelection = libraryManager.multiLibSearch.momentSelection;
  const { openModal } = useMomentModal();

  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const momentId = encodeMomentToString(media.libraryId, media._id, startTime, endTime);
  // Keyed to the provider, not to `isMobile`. The centre line runs wherever the device can't hover,
  // which is not the same set as "narrow": an iPad in landscape is wide and hoverless, and a narrow
  // desktop window is the reverse. Reading `isMobile` here disagreed with the provider in both
  // directions — no reveal on a tablet, and playback pinned off in a narrow window with a mouse.
  const { playbackId, bandIds, register, isActive: isCenteredCardActive } = useCenteredCard();
  /** Level with the nominated moment, so its whole row reveals together. */
  const isInBand = isCenteredCardActive && bandIds.has(momentId);
  /** Nominated to autoplay — null for everyone once more than one moment is revealed. */
  const isPlaybackTarget = isCenteredCardActive && playbackId === momentId;
  const isSelected = momentSelection.selectedMoments.has(momentId);
  const isBaseClip = result.id === libraryManager.multiLibSearch.request?.clipId;
  const { url: src, token: srcToken } = useMediaFilePreview(
    media.libraryId,
    media._id,
    HLS_PLAYLIST_PATH,
  );
  const momentMergeStore = useMomentMergeStore();
  const isAudio = isAudioMedia(media);
  const isCompact = isCompactCell(width);

  // Register this card for center-of-viewport tracking
  useEffect(() => {
    if (!isCenteredCardActive || !cardRef.current) return;
    return register(momentId, cardRef.current);
  }, [isCenteredCardActive, momentId, register]);

  // Play/pause from the centre line without toggling autoPlay (toggling autoPlay causes HLS
  // teardown/rebuild which looks like a reload). Skipped where hover owns playback instead.
  useEffect(() => {
    if (!isCenteredCardActive) return;
    const video = videoRef.current;
    if (!video) return;

    // When autoplay is off, pause everything and bail
    if (!uiPreferences.autoplaymoments) {
      video.pause();
      return;
    }

    if (isPlaybackTarget) {
      const tryPlay = () => video.play().catch(() => {});
      tryPlay();
      // HLS may not have loaded yet (especially with loadDelay), so retry once ready
      video.addEventListener('canplay', tryPlay, { once: true });
      return () => video.removeEventListener('canplay', tryPlay);
    } else {
      video.pause();
    }
  }, [isCenteredCardActive, isPlaybackTarget, uiPreferences.autoplaymoments]);

  const trackMomentToggle = (id: string) => {
    momentSelection.toggleMomentSelection(id as any);
    trackingStore.momentSelectionChanged({
      totalSelected: momentSelection.selectedMoments.size,
    });
  };

  const searchSimilarClip = (resetText: boolean = false) => {
    if (!videoRef.current) return;

    const preview = getThumbnailFromVideo(videoRef.current);
    libraryManager.multiLibSearch.setSearchClip(result.id, preview, resetText);
  };

  useEffect(() => {
    if (isBaseClip && libraryManager.multiLibSearch.searchClipPreview === null) {
      setTimeout(() => {
        searchSimilarClip(false);
      }, 1000);
    }
  }, []);

  return (
    <SelectableCard
      ref={cardRef}
      data-moment-id={momentId}
      className={classNames(
        css.root,
        isSelected && css.selected,
        isBaseClip && !isSelected && css.searchReference,
        isInBand && css.active,
      )}
      isSelected={isSelected}
      onClick={() => {
        if (momentSelection.isSelecting) {
          trackMomentToggle(momentId);
        } else {
          const resultsList = libraryManager.multiLibSearch.results?.results;
          const idx = resultsList?.findIndex((r) => r.id === result.id) ?? -1;
          trackingStore.searchResultClicked({
            mediaId: media._id,
            libraryId: media.libraryId,
            resultIndex: idx,
            resultType: libraryManager.multiLibSearch.resultType,
          });
          openModal(result, media);
        }
      }}
    >
      <VideoPreview
        ref={videoRef}
        src={src || ''}
        start={startTime}
        end={endTime}
        className={css.video}
        // Where the centre line drives playback, VideoPreview must not also autoplay — the effect
        // above owns it, and toggling `autoPlay` churns HLS. Everywhere else hover owns it.
        autoPlay={isCenteredCardActive ? false : uiPreferences.autoplaymoments}
        maxMaxBufferLength={Math.min(endTime - startTime + 1, 30)}
        isMoment
        disableHoverPlay={isCenteredCardActive && !uiPreferences.autoplaymoments}
        priority={10000 - rowIndex * 10 + (10 - columnIndex)}
        libraryId={media.libraryId}
        loadDelay={isMobile ? 400 : 0}
      >
        {isAudio && (
          <div className={css.audioBackground}>
            <PlaceholderGradient id={generateIdFromString(media._id)} blur={200} />
            <div className={css.audioIconOverlay}>
              <AudioSelectedIcon size={48} />
            </div>
          </div>
        )}
        <TopOverlay
          leftChildren={
            <div className={css.iconButton} onClick={(e) => e.stopPropagation()}>
              <SoundToggle />
            </div>
          }
          rightChildren={
            <>
              {['visual', 'unified'].includes(libraryManager.multiLibSearch.resultType) &&
                libraryManager.selectedLibraryIdsToSearch.size === 1 && (
                  <Tooltip title="Search for similar moments" arrow placement="top">
                    <div
                      className={css.iconButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        trackingStore.searchSimilarClicked({
                          mediaId: media._id,
                          libraryId: media.libraryId,
                        });
                        searchSimilarClip();
                        uiPreferences.searchBarFocusCallback?.();
                      }}
                    >
                      <SimilarSearchIcon />
                    </div>
                  </Tooltip>
                )}
              {/* Same label and glyph as the moment preview's CTA — this card hands over the
                  moment's original bounds and the preview hands over the trimmed range, but it
                  is one capability and must not read as two. Both now read the catalog directly;
                  the shared `CLIP_AGENT_LABEL` constant they used to import is gone, because
                  once the wording matched the catalog's it was a second name for one string. */}
              <Tooltip title={VERBS['work-with-agent'].label} arrow placement="top">
                <div
                  className={css.iconButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    trackingStore.searchAskAnAgentClicked({
                      mediaId: media._id,
                      libraryId: media.libraryId,
                    });
                    askAgentAboutMoments(
                      media.libraryId,
                      {
                        libraryId: media.libraryId,
                        searchType: libraryManager.multiLibSearch.resultType as SegmentType,
                        mediaId: media._id,
                        beginTimestamp: startTime,
                        endTimestamp: endTime,
                      },
                      {
                        surface: 'search',
                        control: 'moment-overlay',
                      },
                    );
                  }}
                >
                  {verbMenuIcon('work-with-agent')}
                </div>
              </Tooltip>
              <div className={css.iconButton} onClick={(e) => e.stopPropagation()}>
                <SelectionCheckbox
                  selection={momentSelection}
                  itemId={encodeMomentToString(media.libraryId, media._id, startTime, endTime)}
                  toggle={(itemId) => trackMomentToggle(itemId)}
                />
              </div>
            </>
          }
          baseClassName={css.base}
        />
        <div className={css.bottomTimestampBar}>
          <div
            className={css.momentTimestampBar}
            style={
              {
                '--moment-timestamp-bar-center': media.metadata?.video?.duration
                  ? `${((startTime + (endTime - startTime) / 2) / media.metadata.video.duration) * 100}%`
                  : '0%',
                '--moment-timestamp-bar-width': media.metadata?.video?.duration
                  ? `${((endTime - startTime) / media.metadata.video.duration) * 100}%`
                  : '100%',
              } as React.CSSProperties
            }
          ></div>
        </div>
        <GradientOverlay
          variant="bottom-blur"
          backdropBlur={isMobile ? 10 : 20}
          height={isCompact ? (isMobile ? '20px' : '165px') : '110px'}
          zIndex={1}
          className={css.bottomBlurOverlay}
        />
        <div className={classNames(css.lower, isCompact && !isMobile && css.compact)}>
          <div className={css.left}>
            <ExternalLinkIcon origin={media.origin} />
            <span className={css.momentTitle}>{media.metadata?.name ?? '-'}</span>
          </div>
          <div className={css.right}>
            <span
              className={css.chip}
            >{`${secondsToHMS(startTime)} - ${secondsToHMS(endTime)}`}</span>
            <span
              className={css.chip}
            >{`${secondsToHMS(endTime - startTime, { includeMilliseconds: true, millisecondsPrecision: 2 })}`}</span>
          </div>
        </div>
        <div
          className={classNames(
            css.gradientOverlayContainer,
            isCompact && !isMobile && css.compactGradient,
          )}
        >
          <GradientOverlay borderRadius={14} zIndex={1} height={5} />
        </div>
        <div className={css.middle} onClick={(e) => e.stopPropagation()}>
          {isBaseClip &&
            ['visual', 'unified'].includes(libraryManager.multiLibSearch.resultType) && (
              <div className={css.roundedChip}>
                <div className={css.referencePill}>
                  <span>You searched from this moment</span>
                  <Tooltip
                    title="This moment triggered your current search. To start fresh, clear it from the search bar."
                    placement="top"
                    arrow
                  >
                    <InfoIcon sx={{ fontSize: 15 }} />
                  </Tooltip>
                </div>
              </div>
            )}
        </div>
      </VideoPreview>
    </SelectableCard>
  );
});
